/**
 * SavingsCardInput Component
 * Mobile-friendly card-based input for savings breakdown
 * Each savings item is displayed as a visual card with stacked fields
 */

import React, { useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { SavingsBreakdownItem, SavingsAvailability, SavingsCategory } from '../../api/types';

export interface SavingsTypeOption {
  value: string;
  label: string;
}

export interface AvailabilityOption {
  value: SavingsAvailability;
  label: string;
}

export interface CategoryOption {
  value: SavingsCategory;
  label: string;
}

interface SavingsCardInputProps {
  items: SavingsBreakdownItem[];
  onChangeItems: (items: SavingsBreakdownItem[]) => void;
  typeOptions: SavingsTypeOption[];
  availabilityOptions?: AvailabilityOption[];
  categoryOptions?: CategoryOption[];
  maxLines?: number;
  autoRevealCount?: number; // Number of types to auto-reveal (default: 1 = no auto-reveal)
}

// Default availability options (French labels)
const DEFAULT_AVAILABILITY_OPTIONS: AvailabilityOption[] = [
  { value: 'immediate', label: 'Disponible immédiatement' },
  { value: 'short_term', label: 'Court terme (< 1 mois)' },
  { value: 'medium_term', label: 'Moyen terme (1-12 mois)' },
  { value: 'long_term', label: 'Long terme (> 1 an)' },
  { value: 'blocked', label: 'Bloqué (retraite, PEE...)' },
];

// Default category options (French labels)
const DEFAULT_CATEGORY_OPTIONS: CategoryOption[] = [
  { value: 'liquid', label: 'Liquidité / Urgence' },
  { value: 'investment', label: 'Investissement' },
  { value: 'retirement', label: 'Retraite' },
];

// Savings type defaults: auto-set availability, yield, and category
const SAVINGS_TYPE_DEFAULTS: Record<string, { availability: SavingsAvailability; yield: number; category: SavingsCategory }> = {
  livret_a:            { availability: 'immediate',   yield: 2.4,  category: 'liquid' },
  ldds:                { availability: 'immediate',   yield: 2.4,  category: 'liquid' },
  lep:                 { availability: 'immediate',   yield: 3.5,  category: 'liquid' },
  pel:                 { availability: 'medium_term', yield: 2.25, category: 'liquid' },
  cel:                 { availability: 'medium_term', yield: 2.0,  category: 'liquid' },
  assurance_vie:       { availability: 'long_term',   yield: 2.5,  category: 'investment' },
  pea:                 { availability: 'long_term',   yield: 5.0,  category: 'investment' },
  pea_pme:             { availability: 'long_term',   yield: 5.0,  category: 'investment' },
  per:                 { availability: 'blocked',     yield: 4.0,  category: 'retirement' },
  pee:                 { availability: 'blocked',     yield: 4.0,  category: 'retirement' },
  perco:               { availability: 'blocked',     yield: 4.0,  category: 'retirement' },
  checking_account:    { availability: 'immediate',   yield: 0,    category: 'liquid' },
  savings_account:     { availability: 'immediate',   yield: 0.5,  category: 'liquid' },
  investment_account:  { availability: 'immediate',   yield: 5.0,  category: 'investment' },
  crypto:              { availability: 'immediate',   yield: 0,    category: 'investment' },
  term_deposit:        { availability: 'medium_term', yield: 3.0,  category: 'liquid' },
  other:               { availability: 'immediate',   yield: 0,    category: 'liquid' },
};

export default function SavingsCardInput({
  items,
  onChangeItems,
  typeOptions,
  availabilityOptions = DEFAULT_AVAILABILITY_OPTIONS,
  categoryOptions = DEFAULT_CATEGORY_OPTIONS,
  maxLines = 10,
  autoRevealCount = 1,
}: SavingsCardInputProps) {
  // Track raw input strings for decimal fields (to preserve "1." while typing)
  const [yieldInputs, setYieldInputs] = React.useState<{ [key: number]: string }>({});
  const autoRevealTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoRevealTimeout.current) {
        clearTimeout(autoRevealTimeout.current);
      }
    };
  }, []);

  // Calculate totals
  const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const liquidTotal = items
    .filter(item => item.availability === 'immediate')
    .reduce((sum, item) => sum + (item.amount || 0), 0);

  // Add new savings item
  const handleAddSavings = () => {
    if (items.length >= maxLines) return;

    // Find first unused type or use 'other'
    const usedTypes = items.map(item => item.type);
    const availableType = typeOptions.find(opt => !usedTypes.includes(opt.value))?.value || 'other';

    const defaults = SAVINGS_TYPE_DEFAULTS[availableType];

    onChangeItems([
      ...items,
      {
        type: availableType,
        amount: 0,
        availability: defaults?.availability || 'immediate',
        yield: defaults?.yield || 0,
        category: defaults?.category || 'liquid',
      },
    ]);
  };

  // Remove savings item
  const handleRemoveSavings = (index: number) => {
    if (items.length <= 1) return;
    const newItems = items.filter((_, i) => i !== index);
    onChangeItems(newItems);
  };

  // Update handlers
  const handleTypeChange = (index: number, newType: string) => {
    const newItems = [...items];
    const defaults = SAVINGS_TYPE_DEFAULTS[newType];
    newItems[index] = {
      ...newItems[index],
      type: newType,
      ...(defaults && {
        availability: defaults.availability,
        yield: defaults.yield,
        category: defaults.category,
      }),
    };
    // Clear raw yield input so the new default shows
    setYieldInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[index];
      return newInputs;
    });
    onChangeItems(newItems);
  };

  const handleAmountChange = (index: number, value: string) => {
    const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], amount: numValue };
    onChangeItems(newItems);
  };

  const handleAvailabilityChange = (index: number, value: SavingsAvailability) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], availability: value };

    // Auto-set category based on availability
    if (value === 'immediate' || value === 'short_term') {
      newItems[index].category = 'liquid';
    } else if (value === 'blocked') {
      newItems[index].category = 'retirement';
    }

    onChangeItems(newItems);
  };

  const handleYieldChange = (index: number, value: string) => {
    // Keep only valid decimal characters (digits and one decimal point)
    let cleanValue = value.replace(/[^0-9.,]/g, '');
    // Replace comma with dot for French decimal separator
    cleanValue = cleanValue.replace(',', '.');
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }

    // Store raw string to preserve "1." while typing
    setYieldInputs(prev => ({ ...prev, [index]: cleanValue }));

    // Parse to number for state
    const numValue = parseFloat(cleanValue) || 0;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], yield: numValue };
    onChangeItems(newItems);
  };

  // Get display value for yield input
  const getYieldDisplayValue = (index: number, itemYield?: number): string => {
    if (yieldInputs[index] !== undefined) {
      return yieldInputs[index];
    }
    return itemYield ? itemYield.toString() : '';
  };

  // Auto-reveal next field when user leaves the last item with a value > 0
  const handleAmountBlur = (index: number) => {
    const item = items[index];
    const isLastItem = index === items.length - 1;

    if (
      isLastItem &&
      item.amount > 0 &&
      items.length < autoRevealCount &&
      items.length < maxLines
    ) {
      if (autoRevealTimeout.current) {
        clearTimeout(autoRevealTimeout.current);
      }
      autoRevealTimeout.current = setTimeout(() => {
        const usedTypes = items.map(i => i.type);
        const nextType = typeOptions.find(opt => !usedTypes.includes(opt.value));

        if (nextType) {
          const defaults = SAVINGS_TYPE_DEFAULTS[nextType.value];
          onChangeItems([
            ...items,
            {
              type: nextType.value,
              amount: 0,
              availability: defaults?.availability || 'immediate',
              yield: defaults?.yield || 0,
              category: defaults?.category || 'liquid',
            },
          ]);
        }
      }, 300);
    }
  };

  // Format number for display
  const formatNumber = (num: number): string => {
    if (num === 0) return '';
    return num.toLocaleString('fr-FR');
  };

  // Get label for savings type
  const getTypeLabel = (type: string): string => {
    return typeOptions.find(opt => opt.value === type)?.label || type;
  };

  // Get availability icon
  const getAvailabilityIcon = (availability?: SavingsAvailability): string => {
    switch (availability) {
      case 'immediate':
        return '⚡';
      case 'short_term':
        return '📅';
      case 'medium_term':
        return '📆';
      case 'long_term':
        return '📈';
      case 'blocked':
        return '🔒';
      default:
        return '💰';
    }
  };

  return (
    <View style={styles.container}>
      {items.map((item, index) => (
        <View key={index} style={styles.card}>
          {/* Card Header with Type Picker and Delete */}
          <View style={styles.cardHeader}>
            <Text style={styles.cardIcon}>{getAvailabilityIcon(item.availability)}</Text>
            <View style={styles.pickerWrapper}>
              <Picker
                selectedValue={item.type}
                onValueChange={(value: string) => handleTypeChange(index, value)}
                style={styles.picker}
                itemStyle={styles.pickerItem}
              >
                {typeOptions.map((option) => (
                  <Picker.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </Picker>
            </View>
            {items.length > 1 && (
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={() => handleRemoveSavings(index)}
              >
                <Text style={styles.deleteButtonText}>✕</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Card Body with Fields */}
          <View style={styles.cardBody}>
            {/* Amount Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Valeur actuelle</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={formatNumber(item.amount)}
                  onChangeText={(value) => handleAmountChange(index, value)}
                  onBlur={() => handleAmountBlur(index)}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                />
                <Text style={styles.inputUnit}>€</Text>
              </View>
              <Text style={styles.fieldHint}>Le solde aujourd'hui, pas le montant investi au départ</Text>
            </View>

            {/* Availability Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Disponibilité</Text>
              <View style={styles.pickerFieldWrapper}>
                <Picker
                  selectedValue={item.availability || 'immediate'}
                  onValueChange={(value: SavingsAvailability) => handleAvailabilityChange(index, value)}
                  style={styles.fieldPicker}
                  itemStyle={styles.fieldPickerItem}
                >
                  {availabilityOptions.map((option) => (
                    <Picker.Item
                      key={option.value}
                      label={option.label}
                      value={option.value}
                    />
                  ))}
                </Picker>
              </View>
            </View>

            {/* Yield Field */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Rendement annuel</Text>
              <View style={styles.fieldInput}>
                <TextInput
                  style={styles.input}
                  value={getYieldDisplayValue(index, item.yield)}
                  onChangeText={(value) => handleYieldChange(index, value)}
                  onBlur={() => {
                    // Clear raw input on blur to use formatted value
                    setYieldInputs(prev => {
                      const newInputs = { ...prev };
                      delete newInputs[index];
                      return newInputs;
                    });
                  }}
                  placeholder="0"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="decimal-pad"
                />
                <Text style={styles.inputUnit}>%</Text>
              </View>
            </View>
          </View>
        </View>
      ))}

      {/* Add Savings Button */}
      {items.length < maxLines && (
        <TouchableOpacity style={styles.addButton} onPress={handleAddSavings}>
          <Text style={styles.addButtonText}>+ Ajouter un produit d'épargne</Text>
        </TouchableOpacity>
      )}

      {/* Total Summary */}
      <View style={styles.totalContainer}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Épargne totale</Text>
          <Text style={styles.totalValue}>
            {total.toLocaleString('fr-FR')} €
          </Text>
        </View>
        {liquidTotal > 0 && liquidTotal !== total && (
          <View style={styles.totalRow}>
            <View style={styles.liquidLabelContainer}>
              <Text style={styles.liquidIcon}>⚡</Text>
              <Text style={styles.totalLabelSecondary}>Disponible immédiatement</Text>
            </View>
            <Text style={styles.totalValueSecondary}>
              {liquidTotal.toLocaleString('fr-FR')} €
            </Text>
          </View>
        )}
        {liquidTotal > 0 && (
          <View style={styles.resilienceHint}>
            <Text style={styles.resilienceHintText}>
              💡 Seule l'épargne disponible immédiatement compte pour ta résilience financière
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  card: {
    backgroundColor: colors.background,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: spacing.md,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.success + '10',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingRight: spacing.sm,
    paddingLeft: spacing.xs,
    paddingVertical: 6,
  },
  cardIcon: {
    fontSize: 20,
    marginRight: spacing.xs,
  },
  pickerWrapper: {
    flex: 1,
    height: 36,
    justifyContent: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  picker: {
    height: 36,
    width: '100%',
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingHorizontal: spacing.sm,
  },
  pickerItem: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  deleteButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.error + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.sm,
  },
  deleteButtonText: {
    color: colors.error,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
  },
  cardBody: {
    padding: spacing.md,
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border + '50',
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    flex: 1,
  },
  fieldHint: {
    fontSize: typography.fontSize.xs,
    color: colors.textTertiary,
    marginTop: 2,
    fontStyle: 'italic',
  },
  fieldInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    minWidth: 120,
  },
  pickerFieldWrapper: {
    backgroundColor: colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    minWidth: 180,
    height: 36,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  fieldPicker: {
    height: 36,
    width: '100%',
    backgroundColor: 'transparent',
  },
  fieldPickerItem: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  input: {
    flex: 1,
    fontSize: typography.fontSize.md,
    color: colors.text,
    textAlign: 'right',
    paddingVertical: 4,
    minWidth: 60,
  },
  inputUnit: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
    minWidth: 20,
  },
  addButton: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.success + '15',
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.success + '30',
    borderStyle: 'dashed',
  },
  addButtonText: {
    color: colors.success,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
  totalContainer: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.success + '30',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  totalLabel: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  totalValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.success,
  },
  liquidLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  liquidIcon: {
    fontSize: 14,
    marginRight: spacing.xs,
  },
  totalLabelSecondary: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  totalValueSecondary: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.success,
  },
  resilienceHint: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border + '50',
  },
  resilienceHintText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});
